import { useMemo, useState } from 'react';
import { api, ApiError, formatDate } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { Page, SmallCraftLicence } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const READ_ROLES = ['SHIP_REGISTRAR', 'PORT_OFFICER'];
const REGISTRAR_ROLES = ['SHIP_REGISTRAR'];
const STATUSES = ['FEE_PENDING', 'ACTIVE', 'SUSPENDED', 'REVOKED', 'EXPIRED', 'REJECTED'];
const CHECKLIST_ITEMS = ['Hull condition', 'Life jackets & safety gear', 'Engine & fuel system', 'Navigation lights', 'Registration markings visible'];

/** M22 — small craft (LOCAL_SMALL_CRAFT class, FULL_REGISTRATION only) annual inspection-light licence. */
export function SmallCraft() {
  const { persona } = useAuth();
  const canRead = persona ? persona.roles.some((r) => READ_ROLES.includes(r)) : false;
  const isRegistrar = persona ? persona.roles.some((r) => REGISTRAR_ROLES.includes(r)) : false;
  const [status, setStatus] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showIssue, setShowIssue] = useState(false);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    return `/api/v1/m22/small-craft-licences?${params.toString()}`;
  }, [status]);

  const listQ = useApiQuery<Page<SmallCraftLicence>>(path);

  return (
    <div>
      <PageHeader
        title="Small Craft Licences"
        subtitle="Annual, inspection-light licensing class for local small craft (M22 — SMSA)."
        actions={
          <>
            <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All statuses</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
            {isRegistrar ? (
              <button className="btn-primary" onClick={() => setShowIssue(true)}>
                + Issue licence
              </button>
            ) : null}
          </>
        }
      />

      {!canRead ? <RoleNotice roles={READ_ROLES} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}

      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No small craft licences match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Licence no.</th>
                <th className="table-head">Owner</th>
                <th className="table-head">Last inspection</th>
                <th className="table-head">Valid to</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((l) => (
                <tr key={l.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(l.id)}>
                  <td className="table-cell font-mono text-xs font-semibold">{l.licenceNo}</td>
                  <td className="table-cell text-xs text-slate-500">
                    {l.ownerType} · {l.ownerId}
                  </td>
                  <td className="table-cell text-xs text-slate-500">{formatDate(l.inspectionDate)}</td>
                  <td className="table-cell text-xs text-slate-500">{l.validTo ? formatDate(l.validTo) : '—'}</td>
                  <td className="table-cell">
                    <StatusBadge status={l.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showIssue ? (
        <IssueSmallCraftModal
          onClose={() => setShowIssue(false)}
          onCreated={() => {
            setShowIssue(false);
            listQ.reload();
          }}
        />
      ) : null}

      {selectedId ? (
        <SmallCraftDetailModal
          id={selectedId}
          canAct={isRegistrar}
          onClose={() => setSelectedId(null)}
          onChanged={() => listQ.reload()}
        />
      ) : null}
    </div>
  );
}

/** Shared inspection-form field state for the issue and renew flows — a proper hook (not a plain helper) so React's rules of hooks apply correctly. */
function useInspectionForm() {
  const [inspectionDate, setInspectionDate] = useState('');
  const [inspectionResult, setInspectionResult] = useState<'PASS' | 'FAIL'>('PASS');
  const [inspectorId, setInspectorId] = useState('');
  const [checklist, setChecklist] = useState<Record<string, boolean>>(() => Object.fromEntries(CHECKLIST_ITEMS.map((i) => [i, true])));
  const [failReasons, setFailReasons] = useState('');
  return { inspectionDate, setInspectionDate, inspectionResult, setInspectionResult, inspectorId, setInspectorId, checklist, setChecklist, failReasons, setFailReasons };
}

function IssueSmallCraftModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [vesselRegistrationId, setVesselRegistrationId] = useState('');
  const form = useInspectionForm();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m22/small-craft-licences', {
        vesselRegistrationId: vesselRegistrationId.trim(),
        inspectionDate: new Date(form.inspectionDate).toISOString(),
        inspectionResult: form.inspectionResult,
        inspectorId: form.inspectorId.trim(),
        checklist: form.checklist,
        failReasons: form.inspectionResult === 'FAIL' ? form.failReasons.split(',').map((s) => s.trim()).filter(Boolean) : [],
      });
      onCreated();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to issue licence');
    } finally {
      setBusy(false);
    }
  };

  const valid = vesselRegistrationId.trim() && form.inspectionDate && form.inspectorId.trim();

  return (
    <Modal title="Issue a small craft licence" onClose={onClose} wide>
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <p className="mb-3 text-xs text-slate-500">Vessel must be class LOCAL_SMALL_CRAFT and status FULL_REGISTRATION — find its id in the Vessel Registry.</p>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Field label="Vessel registration id" className="col-span-2 sm:col-span-3">
          <input className="field-input" value={vesselRegistrationId} onChange={(e) => setVesselRegistrationId(e.target.value)} />
        </Field>
        <Field label="Inspection date">
          <input className="field-input" type="date" value={form.inspectionDate} onChange={(e) => form.setInspectionDate(e.target.value)} />
        </Field>
        <Field label="Inspector id">
          <input className="field-input" value={form.inspectorId} onChange={(e) => form.setInspectorId(e.target.value)} />
        </Field>
        <Field label="Result">
          <select className="field-input" value={form.inspectionResult} onChange={(e) => form.setInspectionResult(e.target.value as 'PASS' | 'FAIL')}>
            <option value="PASS">PASS</option>
            <option value="FAIL">FAIL</option>
          </select>
        </Field>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        {CHECKLIST_ITEMS.map((item) => (
          <label key={item} className="flex items-center gap-2 text-xs text-slate-600">
            <input type="checkbox" checked={form.checklist[item] ?? true} onChange={(e) => form.setChecklist((c) => ({ ...c, [item]: e.target.checked }))} />
            {item}
          </label>
        ))}
      </div>
      {form.inspectionResult === 'FAIL' ? (
        <Field label="Fail reasons (comma-separated)" className="mt-3">
          <input className="field-input" value={form.failReasons} onChange={(e) => form.setFailReasons(e.target.value)} />
        </Field>
      ) : null}
      <button className={`mt-4 ${form.inspectionResult === 'PASS' ? 'btn-primary' : 'btn-danger'}`} disabled={busy || !valid} onClick={submit}>
        {busy ? 'Submitting…' : form.inspectionResult === 'PASS' ? 'Issue licence (creates fee intent)' : 'Record failed inspection (rejects)'}
      </button>
    </Modal>
  );
}

function SmallCraftDetailModal({ id, canAct, onClose, onChanged }: { id: string; canAct: boolean; onClose: () => void; onChanged: () => void }) {
  const q = useApiQuery<SmallCraftLicence>(`/api/v1/m22/small-craft-licences/${id}`);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [showAction, setShowAction] = useState<'suspend' | 'revoke' | 'renew' | null>(null);
  const form = useInspectionForm();

  const call = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      q.reload();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  const l = q.data;

  return (
    <Modal title={l ? `Small craft licence ${l.licenceNo}` : 'Small craft licence'} onClose={onClose} wide>
      {q.error ? <ErrorNote message={q.error} /> : null}
      {q.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {l ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <Detail label="Status" value={<StatusBadge status={l.status} />} />
            <Detail label="Owner" value={`${l.ownerType} · ${l.ownerId}`} />
            <Detail label="Last inspection" value={`${formatDate(l.inspectionDate)} (${l.inspectionResult})`} />
            <Detail label="Inspector" value={l.inspectorId} />
            <Detail label="Valid from" value={l.validFrom ? formatDate(l.validFrom) : '—'} />
            <Detail label="Valid to" value={l.validTo ? formatDate(l.validTo) : '—'} />
          </div>
          {l.rejectionReason ? <ErrorNote message={`Rejected: ${l.rejectionReason}`} /> : null}
          {l.suspendedReason ? <ErrorNote message={`Suspended: ${l.suspendedReason}`} /> : null}

          {canAct ? (
            <div className="space-y-3 rounded-lg border border-slate-200 p-3">
              {l.status === 'ACTIVE' ? (
                showAction === 'renew' ? (
                  <div className="space-y-2">
                    <div className="text-sm font-semibold text-slate-700">Annual renewal inspection</div>
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                      <Field label="Inspection date">
                        <input className="field-input" type="date" value={form.inspectionDate} onChange={(e) => form.setInspectionDate(e.target.value)} />
                      </Field>
                      <Field label="Inspector id">
                        <input className="field-input" value={form.inspectorId} onChange={(e) => form.setInspectorId(e.target.value)} />
                      </Field>
                      <Field label="Result">
                        <select className="field-input" value={form.inspectionResult} onChange={(e) => form.setInspectionResult(e.target.value as 'PASS' | 'FAIL')}>
                          <option value="PASS">PASS</option>
                          <option value="FAIL">FAIL</option>
                        </select>
                      </Field>
                    </div>
                    <button
                      className={form.inspectionResult === 'PASS' ? 'btn-success' : 'btn-danger'}
                      disabled={busy || !form.inspectionDate || !form.inspectorId.trim()}
                      onClick={() =>
                        call(() =>
                          api.post(`/api/v1/m22/small-craft-licences/${l.id}/renew`, {
                            inspectionDate: new Date(form.inspectionDate).toISOString(),
                            inspectionResult: form.inspectionResult,
                            inspectorId: form.inspectorId.trim(),
                            checklist: form.checklist,
                            failReasons: form.inspectionResult === 'FAIL' ? form.failReasons.split(',').map((s) => s.trim()).filter(Boolean) : [],
                          }),
                        )
                      }
                    >
                      {busy ? 'Submitting…' : 'Submit renewal inspection'}
                    </button>
                  </div>
                ) : (
                  <button className="btn-secondary" onClick={() => setShowAction('renew')}>
                    Start annual renewal
                  </button>
                )
              ) : null}

              {l.status === 'ACTIVE' ? (
                showAction === 'suspend' ? (
                  <div className="space-y-2">
                    <Field label="Suspension reason">
                      <input className="field-input" value={reason} onChange={(e) => setReason(e.target.value)} />
                    </Field>
                    <button
                      className="btn-danger"
                      disabled={busy || reason.trim().length < 3}
                      onClick={() => call(() => api.post(`/api/v1/m22/small-craft-licences/${l.id}/suspend`, { reason: reason.trim() }))}
                    >
                      Confirm suspend
                    </button>
                  </div>
                ) : (
                  <button className="btn-danger" onClick={() => setShowAction('suspend')}>
                    Suspend
                  </button>
                )
              ) : null}

              {l.status === 'SUSPENDED' ? (
                <button className="btn-success" disabled={busy} onClick={() => call(() => api.post(`/api/v1/m22/small-craft-licences/${l.id}/reinstate`))}>
                  Reinstate
                </button>
              ) : null}

              {l.status === 'ACTIVE' || l.status === 'SUSPENDED' ? (
                showAction === 'revoke' ? (
                  <div className="space-y-2">
                    <Field label="Revocation reason">
                      <input className="field-input" value={reason} onChange={(e) => setReason(e.target.value)} />
                    </Field>
                    <button
                      className="btn-danger"
                      disabled={busy || reason.trim().length < 3}
                      onClick={() => call(() => api.post(`/api/v1/m22/small-craft-licences/${l.id}/revoke`, { reason: reason.trim() }))}
                    >
                      Confirm revoke
                    </button>
                  </div>
                ) : (
                  <button className="btn-danger" onClick={() => setShowAction('revoke')}>
                    Revoke
                  </button>
                )
              ) : null}
            </div>
          ) : (
            <RoleNotice roles={REGISTRAR_ROLES} />
          )}
        </div>
      ) : null}
    </Modal>
  );
}
